'use client';

import { Button } from '@/components/ui/button';
import { ArrowRight, Check, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

export function CoverageComparison() {
  const [showCovered, setShowCovered] = useState(false);

  const toggleView = () => {
    setShowCovered(!showCovered);
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl font-bold mb-4">Smart drivers get covered</h2>
          <p className="text-xl text-gray-600">A vehicle service contract covers what insurance does not.</p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Invoice Card with WarrantyPilot styling */}
            <div className="relative">
              <div
                className={`rounded-xl shadow-lg p-4 transition-all duration-500 ${showCovered ? 'bg-gradient-to-br from-white to-green-50' : 'bg-white'} h-[520px] max-w-md mx-auto relative`}
              >
                <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
                  <div
                    className={`absolute top-0 right-0 transform rotate-45 translate-x-[25%] -translate-y-[50%] w-20 h-5 ${showCovered ? 'bg-[#00B67A]' : 'bg-red-500'}`}
                  ></div>
                </div>

                <div className="relative z-10">
                  <div className="flex items-center mb-3">
                    <div className={`w-1 h-8 mr-2 rounded-full ${showCovered ? 'bg-[#00B67A]' : 'bg-red-500'}`}></div>
                    <h3 className={`text-lg font-bold ${showCovered ? 'text-[#00B67A]' : 'text-gray-700'}`}>
                      Car Repair Invoice
                    </h3>
                  </div>

                  <div className="flex justify-between mb-4 text-sm">
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <p className="text-xs text-gray-500">Billed to</p>
                      <p className="font-medium">Riley Jones</p>
                      <p className="text-xs text-gray-500">123 First St.</p>
                    </div>
                    <div className="bg-gray-50 p-2 rounded-lg text-right">
                      <p className="text-xs text-gray-500">Invoice #</p>
                      <p className="font-medium">#AB2324-01</p>
                    </div>
                  </div>

                  <div className="mb-4 text-sm">
                    <div className="flex justify-between text-xs font-medium text-gray-500 mb-1 px-2 py-1 bg-gray-100 rounded-t-lg">
                      <span>ITEM</span>
                      <span>PRICE</span>
                    </div>
                    <div className="space-y-1 divide-y divide-gray-100">
                      <div className="flex justify-between px-2 py-1">
                        <span>Engine</span>
                        <span className="font-medium">$3,500.00</span>
                      </div>
                      <div className="flex justify-between px-2 py-1">
                        <span>Suspension</span>
                        <span className="font-medium">$1,500.00</span>
                      </div>
                      <div className="flex justify-between px-2 py-1">
                        <span>Electrical</span>
                        <span className="font-medium">$1,200.00</span>
                      </div>
                      <div className="flex justify-between px-2 py-1">
                        <span>Air Conditioning</span>
                        <span className="font-medium">$1,200.00</span>
                      </div>
                      <div className="flex justify-between px-2 py-1">
                        <span>Transmission</span>
                        <span className="font-medium">$2,800.00</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 bg-gray-50 p-3 rounded-lg text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">Subtotal</span>
                      <span className="font-medium">$10,200.00</span>
                    </div>
                    <AnimatePresence mode="wait">
                      {showCovered ? (
                        <motion.div
                          key="covered"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                          className="flex justify-between text-[#00B67A] font-medium"
                        >
                          <span>With Coverage</span>
                          <span>-$10,100.00</span>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="not-covered"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                          className="flex justify-between text-red-500 font-medium"
                        >
                          <span>Without Coverage</span>
                          <span>-$0</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div className="flex justify-between font-bold text-base pt-2 border-t border-gray-200">
                      <span>Total</span>
                      <AnimatePresence mode="wait">
                        {showCovered ? (
                          <motion.span
                            key="covered-total"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                            className="text-[#00B67A]"
                          >
                            $100.00
                          </motion.span>
                        ) : (
                          <motion.span
                            key="not-covered-total"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                            className="text-red-500"
                          >
                            $10,200.00
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="mt-3 flex justify-center">
                    {showCovered ? (
                      <div className="inline-flex items-center px-2 py-1 bg-green-100 text-[#00B67A] rounded-full text-xs font-medium">
                        <Check className="w-3 h-3 mr-1" />
                        With Coverage
                      </div>
                    ) : (
                      <div className="inline-flex items-center px-2 py-1 bg-red-100 text-red-500 rounded-full text-xs font-medium">
                        <X className="w-3 h-3 mr-1" /> Without Coverage
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <AnimatePresence mode="wait" initial={false}>
              {showCovered ? (
                <motion.div
                  key="covered-benefits"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6 h-[520px] flex flex-col justify-center"
                >
                  <h3 className="text-2xl font-bold text-gray-800">With Coverage:</h3>
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#00B67A]/10 flex items-center justify-center">
                        <Check className="w-6 h-6 text-[#00B67A]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg text-gray-800">Flexible Coverage</h4>
                        <p className="text-gray-600">
                          Find a plan that perfectly fits your vehicle and budget. With multiple options to choose from,
                          you can customize your protection to match your specific needs.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#00B67A]/10 flex items-center justify-center">
                        <Check className="w-6 h-6 text-[#00B67A]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg text-gray-800">Cost-Effective Protection</h4>
                        <p className="text-gray-600">
                          Save up to 60% compared to dealer prices. Our marketplace model creates competition among
                          providers, resulting in better rates for you.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#00B67A]/10 flex items-center justify-center">
                        <Check className="w-6 h-6 text-[#00B67A]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg text-gray-800">Hassle-Free Repair</h4>
                        <p className="text-gray-600">
                          Our partner network gives you up to 25% discounts on automotive services. Finally, a simple
                          way to compare service professionals, get upfront pricing, and book service with ease.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="not-covered"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6 h-[520px] flex flex-col justify-center"
                >
                  <h3 className="text-2xl font-bold text-gray-800">Without Coverage:</h3>
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <X className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg text-gray-800">Unexpected Repair Costs</h4>
                        <p className="text-gray-600">
                          A sudden transmission failure could leave you with a repair bill of $2,500 or more—money you
                          weren't planning to spend.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <X className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg text-gray-800">No Protection Against Inflation</h4>
                        <p className="text-gray-600">
                          The cost of parts and labor is rising every year. Without a VSC, you're paying future repair
                          costs at tomorrow's higher prices.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <X className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg text-gray-800">Stress and Inconvenience</h4>
                        <p className="text-gray-600">
                          Driving without protection can lead to unexpected costs and significant stress.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="mt-10 flex justify-center">
            <Button
              onClick={toggleView}
              className="bg-[#00B67A] hover:bg-[#008C5E] text-white px-6 py-3 rounded-lg flex items-center gap-2 text-lg transition-all duration-300"
            >
              {showCovered ? 'See without coverage' : 'See with coverage'}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
